<script lang="ts" setup>
import { toRefs } from 'vue';

import type { NormalizedPerpsPosition } from '@exchange/libs/account/service/src/lib/wss-account-messages';
import { usePositionsListDesktop } from '@exchange/libs/futures/ui/src/lib/useFuturesPositionsList';

import OpenPositionsItem from './OpenPositionsItem.vue';
import DivTable from '../components/DivTable.vue';
import InfoMessageBlock from '../components/InfoMessageBlock.vue';

const props = defineProps({
  authenticatedUser: { type: Boolean, required: true },
  breakpoint: { type: String, required: true },
  dragging: { type: Boolean, required: true },
  marketId: { type: String, required: true },
  showAllMarkets: { type: Boolean, required: true },
  maxLeverage: { type: Number, required: false },
  futuresPositions: { type: Array, required: true },
});

const { breakpoint: breakpointRef, marketId } = toRefs(props);

const tableSortingKey = 'MY_POSITIONS_SORTING';

const { desktopHeader } = usePositionsListDesktop(tableSortingKey, breakpointRef);

const emit = defineEmits(['close-position', 'flip-position']);
</script>

<template>
  <div-table
    class="open-positions-list"
    :class="breakpoint"
    :header="desktopHeader"
    :rows="futuresPositions"
    :sticky-header="true"
    @header-changed="() => {}"
  >
    <template #default="{ rows }">
      <open-positions-item
        v-for="position in rows as Array<NormalizedPerpsPosition>"
        :key="position.instrument_code"
        class="open-positions-list__row"
        :position="position"
        :max-leverage="maxLeverage"
        @close-position="(pos) => emit('close-position', pos)"
        @flip-position="(pos) => emit('flip-position', pos)"
      />
      <info-message-block
        v-if="!rows.length"
        class="open-positions-list__info"
        :market-id="marketId"
        :show-all-markets="showAllMarkets"
        :authenticated-user="authenticatedUser"
        :is-positions="true"
      />
    </template>
  </div-table>
</template>

<style lang="scss">
.open-positions-list {
  width: 100%;

  --market-width: minmax(120px, 0.6fr);
  --side-width: minmax(50px, 0.4fr);
  --size-width: minmax(100px, 0.6fr);
  --position-value-width: minmax(100px, 0.6fr);
  --average-price-width: minmax(100px, 0.6fr);
  --mark-price-width: minmax(100px, 0.6fr);
  --last-settlement-width: minmax(100px, 0.6fr);
  --realised-pnl-width: minmax(100px, 0.6fr);
  --unrealised-pnl-width: minmax(100px, 0.6fr);
  --margin-used-width: minmax(100px, 0.6fr);
  --close-position-width: minmax(40px, 0.3fr);
  --grid-table-columns: var(--market-width) var(--side-width) var(--size-width) var(--position-value-width) var(--average-price-width) var(--mark-price-width)
    var(--last-settlement-width) var(--realised-pnl-width) var(--unrealised-pnl-width) var(--margin-used-width) var(--close-position-width);

  &__row {
    grid-template-columns: var(--grid-table-columns);
  }
}
</style>
